import type { ActionResult } from './types.js';

/**
 * Generic mutation function for Tanstack Query that wraps an async function returning an ActionResult.
 * Ensures type safety and consistent error handling for mutations.
 *
 * @template TArgs Arguments tuple type
 * @template TResult Return type of the mutation
 * @param fn The async mutation function to call
 * @returns A mutation function compatible with Tanstack Query
 */
export function createMutationFn<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<ActionResult<TResult>>
) {
  return async (...args: TArgs): Promise<TResult> => {
    const result = await fn(...args);

    if (result.status === 'ERROR') {
      throw new Error(result.error, { cause: result.reason });
    }

    return result.data;
  };
}
